import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import pnlService from "../../Services/pnlService";

const ByDate = () => {
  const currentYear = new Date().getFullYear();

  const [medicines, setMedicines] = useState([]);
  const [checkup, setCheckup] = useState([]);
  const [pharmacy, setPharmacy] = useState([]);
  const [services, setServices] = useState([]);

  const [combinedData, setCombinedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [expandedMonths, setExpandedMonths] = useState({});

  // ✅ Format helper
  const fmt = (n) => {
    const v = isNaN(n) ? 0 : Math.round(parseFloat(n) * 100) / 100;
    return v.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ✅ Fetch all data
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [medRes, checkRes, pharmRes, servRes] = await Promise.all([
          pnlService.fetchMedicinebyMonth(),
          pnlService.fetchCheckupbyMonth(),
          pnlService.fetchPharmacybyMonth(),
          pnlService.fetchServicesbyMonth(),
        ]);
        setMedicines(medRes?.Medicines || []);
        setCheckup(checkRes?.Checkup || []);
        setPharmacy(pharmRes?.Pharmacy || []);
        setServices(servRes?.Services || []);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        toast.error("Error fetching data");
      }
    };
    fetchAll();
  }, []);

  // ✅ Combine data
  useEffect(() => {
    const monthsOrder = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const data = monthsOrder.map((month) => {
      const med = medicines.find(
        (m) =>
          m.month_name === month && Number(m.year) === Number(selectedYear),
      );
      const chk = checkup.find(
        (c) =>
          c.month_name === month && Number(c.year) === Number(selectedYear),
      );
      const srv = services.find(
        (s) =>
          s.month_name === month && Number(s.year) === Number(selectedYear),
      );
      const ph = pharmacy.find(
        (p) =>
          p.month_name === month && Number(p.year) === Number(selectedYear),
      );

      const checkupAmount = parseFloat(chk?.total_checkup_fee || 0);
      const medicineAmount = parseFloat(med?.total_medicines_sale || 0);
      const servicesAmount = parseFloat(
        srv?.total_services_sale ?? srv?.total_medicines_sale ?? 0,
      );
      const pharmacyAmount = parseFloat(ph?.total_amount || 0);
      const total =
        checkupAmount + medicineAmount + servicesAmount + pharmacyAmount;

      return {
        month,
        checkupAmount,
        medicineAmount,
        servicesAmount,
        pharmacyAmount,
        total,
      };
    });

    setCombinedData(data.filter((item) => item.total > 0));
  }, [medicines, checkup, pharmacy, services, selectedYear]);

  // ✅ Totals
  const calculateTotal = (key) =>
    combinedData.reduce((sum, item) => sum + (item[key] || 0), 0);

  const grandTotal = calculateTotal("total");

  const categories = [
    { label: "Checkup", key: "checkupAmount" },
    { label: "Medicines", key: "medicineAmount" },
    { label: "Services", key: "servicesAmount" },
    { label: "Pharmacy", key: "pharmacyAmount" },
  ];

  // ✅ Toggle mobile card
  const toggleMonth = (month) => {
    setExpandedMonths((prev) => ({ ...prev, [month]: !prev[month] }));
  };

  // ✅ Column widths for desktop
  const colWidths = ["130px", "100px", "100px", "100px", "100px", "120px"];

  const ColGroup = () => (
    <colgroup>
      {colWidths.map((w, i) => (
        <col key={i} style={{ width: w, minWidth: w }} />
      ))}
    </colgroup>
  );

  return (
    <div className="p-3 sm:p-4 font-sans">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs font-medium tracking-widest uppercase text-gray-500">
          Yearly Income Summary
        </p>
        <select
          className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
        >
          {[2024, 2025, 2026].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-4">
        {categories.map(({ label, key }) => (
          <div
            key={label}
            className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5"
          >
            <p className="text-[10px] tracking-wide text-gray-400 uppercase mb-1">
              {label}
            </p>
            <p className="text-base font-semibold text-gray-800">
              {fmt(calculateTotal(key))}
            </p>
          </div>
        ))}
        <div className="bg-gray-100  rounded-lg px-3 py-2.5 border-t-2 border-[#F07F1C] col-span-2 sm:col-span-3 lg:col-span-1">
          <p className="text-[10px] tracking-wide text-gray-400 uppercase mb-1">
            Grand Total
          </p>
          <p className="text-base font-bold text-[#F07F1C]">
            {fmt(grandTotal)}
          </p>
        </div>
      </div>

      {/* ══════════════════════════════════════
          MOBILE VIEW — Collapsible Month Cards
          (shown on screens < 640px)
      ══════════════════════════════════════ */}
      <div className="block sm:hidden">
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-xs">
            Loading...
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {combinedData.map((item) => {
                const isOpen = !!expandedMonths[item.month];
                return (
                  <div
                    key={item.month}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    {/* Card Header */}
                    <button
                      onClick={() => toggleMonth(item.month)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 text-left"
                    >
                      <span className="text-[12px] font-medium text-gray-700">
                        {item.month}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-[#F07F1C]">
                          {fmt(item.total)}
                        </span>
                        <svg
                          className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>

                    {/* Card Body */}
                    {isOpen && (
                      <div className="px-3 py-2 bg-white divide-y divide-gray-100">
                        {categories.map(({ label, key }) => (
                          <div
                            key={label}
                            className="flex items-center justify-between py-2"
                          >
                            <span className="text-[12px] text-gray-500 w-20">
                              {label}
                            </span>
                            <span className="text-[12px] font-medium text-gray-800">
                              {fmt(item[key])}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Mobile Grand Total Footer */}
            <div className="mt-3 bg-[#1F2937] rounded-xl px-3 py-3">
              {categories.map(({ label, key }) => (
                <div
                  key={label}
                  className="flex items-center justify-between py-1.5 border-b border-white/10 last:border-0"
                >
                  <span className="text-[12px] text-gray-400">{label}</span>
                  <span className="text-[12px] text-gray-100 font-medium">
                    {fmt(calculateTotal(key))}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 mt-1 border-t border-white/20">
                <span className="text-[12px] font-medium text-gray-100">
                  Grand Total
                </span>
                <span className="text-[14px] font-bold text-[#F07F1C]">
                  {fmt(grandTotal)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ══════════════════════════════════════
          DESKTOP VIEW — Single table, sticky thead + tfoot
          (shown on screens >= 640px)
      ══════════════════════════════════════ */}
      <div className="hidden sm:block border rounded-xl overflow-hidden">
        <div className="overflow-y-auto" style={{ maxHeight: "520px" }}>
          <table
            className="w-full text-sm"
            style={{
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <ColGroup />

            {/* STICKY HEADER */}
            <thead>
              <tr>
                <th className="sticky top-0 z-20 bg-[#1F2937] text-left px-4 py-2.5 text-[12px] text-gray-100 uppercase border-r border-white/10">
                  Month
                </th>
                <th className="sticky top-0 z-20 bg-[#1F2937] text-center px-4 py-2.5 text-[12px] text-gray-100 uppercase border-r border-white/10">
                  Checkup
                </th>
                <th className="sticky top-0 z-20 bg-[#1F2937] text-center px-4 py-2.5 text-[12px] text-gray-100 uppercase border-r border-white/10">
                  Medicines
                </th>
                <th className="sticky top-0 z-20 bg-[#1F2937] text-center px-4 py-2.5 text-[12px] text-gray-100 uppercase border-r border-white/10">
                  Services
                </th>
                <th className="sticky top-0 z-20 bg-[#1F2937] text-center px-4 py-2.5 text-[12px] text-gray-100 uppercase border-r border-white/10">
                  Pharmacy
                </th>
                <th className="sticky top-0 z-20 bg-[#1F2937] text-right px-4 py-2.5 text-[12px] text-gray-100 uppercase">
                  Total
                </th>
              </tr>
            </thead>

            {/* BODY */}
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : (
                combinedData.map((item, i) => (
                  <tr
                    key={item.month}
                    className={`border-b border-gray-100 hover:bg-green-50 transition-colors ${
                      i % 2 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <td className="px-4 py-2 border-r border-gray-100 text-[12px] text-gray-600">
                      {item.month}
                    </td>
                    <td className="px-4 py-2 text-center border-r border-gray-100 text-[12px] text-gray-700">
                      {fmt(item.checkupAmount)}
                    </td>
                    <td className="px-4 py-2 text-center border-r border-gray-100 text-[12px] text-gray-700">
                      {fmt(item.medicineAmount)}
                    </td>
                    <td className="px-4 py-2 text-center border-r border-gray-100 text-[12px] text-gray-700">
                      {fmt(item.servicesAmount)}
                    </td>
                    <td className="px-4 py-2 text-center border-r border-gray-100 text-[12px] text-gray-700">
                      {fmt(item.pharmacyAmount)}
                    </td>
                    <td className="px-4 py-2 text-right text-[12px] font-semibold text-gray-800">
                      {fmt(item.total)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {/* STICKY FOOTER */}
            <tfoot>
              <tr className="bg-[#1F2937] text-gray-100">
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-left text-[12px] font-medium border-t border-white/10">
                  Grand Total
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-center text-[12px] font-medium border-t border-white/10">
                  {fmt(calculateTotal("checkupAmount"))}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-center text-[12px] font-medium border-t border-white/10">
                  {fmt(calculateTotal("medicineAmount"))}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-center text-[12px] font-medium border-t border-white/10">
                  {fmt(calculateTotal("servicesAmount"))}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-center text-[12px] font-medium border-t border-white/10">
                  {fmt(calculateTotal("pharmacyAmount"))}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-right text-[12px] font-semibold border-t border-white/10">
                  {fmt(grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ByDate;

